import { FC, useContext, useState } from 'react';
import { Input, Typography, Empty, Tooltip, Divider, message } from 'antd';
import { WorkflowEditorUIDescription, DESCRIPTION_LIMITS } from '#types/System.Parameters.types';
import { LayoutEditorContext } from '../../providers/editor/LayoutEditorContext';
import { RecordGroup, AgentType } from '#types/System.types';
import { Timestamp } from 'firebase/firestore';
import FlexCol from '../atoms/FlexCol';
import FlexBox from '../atoms/FlexBox';
import { RecordGroupsList } from './RecordGroupsList';
import { RecordGroupModal } from './RecordGroupModal';

const { Text } = Typography;
const { TextArea } = Input;

/**
 * Component for editing form/entity descriptions and managing entity record groups.
 * Provides UI for editing labels, descriptions, and record group configurations.
 */
export const LayoutEditorDescription: FC = () => {
    const [modalVisible, setModalVisible] = useState(false);
    const [editingGroup, setEditingGroup] = useState<{ id: string; group: RecordGroup } | null>(null);

    const [messageApi, contextHolder] = message.useMessage();
    const editor = useContext(LayoutEditorContext);
    if (!editor) throw new Error('Editor must be used within LayoutEditorProvider');
    const {
        description,
        type,
        structure,
        handleDescriptionChange,
    } = editor;

    const recordGroups = type === 'entity' && 'recordGroups' in editor ? editor.recordGroups : undefined;
    const handleRecordGroupsChange =
        type === 'entity' && 'handleRecordGroupsChange' in editor
            ? editor.handleRecordGroupsChange
            : undefined;


    if (!structure) {
        return (
            <FlexBox stretch justifyCenter>
                <Empty description={`Select a ${type === 'form' ? 'Form' : 'Record'} to begin editing.`} />
            </FlexBox>
        );
    }

    const handleAddGroup = () => {
        setEditingGroup(null);
        setModalVisible(true);
    };

    const handleEditGroup = (groupId: string, group: RecordGroup) => {
        setEditingGroup({ id: groupId, group });
        setModalVisible(true);
    };

    const handleDeleteGroup = (groupId: string) => {
        if (!handleRecordGroupsChange || !recordGroups) return;

        const updatedGroups = { ...recordGroups };
        delete updatedGroups[groupId];
        handleRecordGroupsChange(updatedGroups);
        messageApi.warning('Record group set to be deleted. Click Save to persist changes.');
    };

    const handleSaveGroup = (groupId: string, group: RecordGroup) => {
        if (!handleRecordGroupsChange || !recordGroups) return;

        const user = editor && 'user' in editor ? editor.user : undefined;

        const now = Timestamp.now();
        const existingGroup = editingGroup ? recordGroups[editingGroup.id] : null;

        const groupWithMeta: RecordGroup = {
            ...group,
            meta: {
                ...(existingGroup?.meta || {}),
                created: existingGroup?.meta?.created || now,
                lastModified: now,
                userId: user?.uid,
                agentType: AgentType.User
            }
        };

        const updatedGroups = { ...recordGroups, [groupId]: groupWithMeta };
        handleRecordGroupsChange(updatedGroups);
        setModalVisible(false);
        messageApi.warning(editingGroup ? 'Record group updated. Click Save to persist changes.' : 'Record group created. Click Save to persist changes.');
    };

    return (
        <FlexCol>
            {contextHolder}
            <FlexCol>
                <FlexCol gap={0}>
                    <Tooltip title={WorkflowEditorUIDescription.shortLabel.longDescription}>
                        <Text>Label</Text>
                    </Tooltip>
                    <Input
                        variant='filled'
                        maxLength={DESCRIPTION_LIMITS.shortLabel}
                        value={description?.shortLabel || ''}
                        onChange={e => handleDescriptionChange('shortLabel', e.target.value)}
                        placeholder={`Enter ${type === 'form' ? 'form' : 'record'} label`}
                    />
                </FlexCol>
                <FlexCol gap={0}>
                    <Tooltip title={WorkflowEditorUIDescription.shortDescription.longDescription}>
                        <Text>Description</Text>
                    </Tooltip>
                    <TextArea
                        autoSize={{ minRows: 3, maxRows: 5 }}
                        variant='filled'
                        maxLength={DESCRIPTION_LIMITS.longDescription}
                        value={description?.longDescription || ''}
                        onChange={e => handleDescriptionChange('longDescription', e.target.value)}
                        placeholder={`Describe what this ${type === 'form' ? 'form' : 'record'} is used for`}
                    />
                </FlexCol>
            </FlexCol>

            {type === 'entity' && (
                <>
                    <Divider />
                    <RecordGroupsList
                        recordGroups={recordGroups}
                        onAdd={handleAddGroup}
                        onEdit={handleEditGroup}
                        onDelete={handleDeleteGroup}
                    />
                </>
            )}

            <RecordGroupModal
                visible={modalVisible}
                editingGroup={editingGroup}
                existingGroups={recordGroups}
                onSave={handleSaveGroup}
                onCancel={() => setModalVisible(false)}
            />
        </FlexCol>
    );
}; 